import { ComboBox } from "@xola/ui-kit";
import clsx from "clsx";
import React, { useEffect, useRef, useState } from "react";
import { Tag, TagType } from "types";
import { CopyableField } from "@/components/Common/CopyableField";

interface TagInputProps {
    value: Tag[];
    placeholder?: string;
    isDisabled?: boolean;
    onChange: (value: Tag[]) => void;
}

interface Option {
    value: string;
    label: string;
    isFixed?: boolean;
    type?: TagType;
}

interface CopyableMultiValueLabelProps {
    data: Option;
}

const CopyableMultiValueLabel = ({ data }: CopyableMultiValueLabelProps) => {
    const { label, data: contentToCopy } = parseLabelData(data);

    const handleClick = () => {
        navigator.clipboard.writeText(contentToCopy);
    };

    return (
        <CopyableField
            data={contentToCopy}
            classNames={{
                outerSpan: clsx(data.isFixed ? null : "max-w-[calc(100%-20px)]"),
                badge: "left-auto  z-10 translate-x-full -right-1",
                tooltipContent: "text-sm",
            }}
            size="small"
            showCopyIcon={false}
            toolTipPlacement="bottom"
            dataTestId="purchase-tags"
        >
            <div
                className={clsx(
                    "relative box-border cursor-pointer overflow-hidden text-ellipsis whitespace-nowrap rounded-sm py-0.5 pl-1.5 pr-1 text-sm text-black",
                )}
                onClick={handleClick}
            >
                {label}
            </div>
        </CopyableField>
    );
};

export const TagInput = ({ value, onChange, placeholder = "Add tag", isDisabled = false }: TagInputProps) => {
    const [inputValue, setInputValue] = useState("");

    const [width, setWidth] = useState(0);
    const innerRef = useRef<HTMLDivElement>(null);

    const handleChange = (value: Option[]) => {
        onChange(value.map((value) => ({ id: value.value })));
    };

    const options: Option[] = value.map((value) => {
        return { value: value.id, label: value.id, type: value.type, isFixed: value.system };
    });

    // Overriding React-Select styling to hide X for system items
    const styles = {
        multiValueRemove: (base: any, state: any) => {
            return state.data.isFixed ? { ...base, display: "none" } : base;
        },
        container: (css: any) => ({ ...css, width: `${width}px` }),
    };

    const handleSubmit = (e: any) => {
        const tagValue = e.target.value.trim();
        if (tagValue.length > 0) {
            onChange([...value, { id: tagValue }]);
        }
    };

    const handleKeyDown = (e: any) => {
        if (e.key === "Enter") {
            handleSubmit(e);
            setInputValue("");
        }
    };

    const handleInputChange = (value: string) => {
        setInputValue(value);
    };

    useEffect(() => {
        const currentRef = innerRef.current;

        const resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const { width } = entry.contentRect;
                setWidth(width);
            }
        });

        if (currentRef) {
            resizeObserver.observe(currentRef);
        }

        return () => {
            if (currentRef) {
                resizeObserver.unobserve(currentRef);
            }
        };
    }, [innerRef]);
    return (
        <div ref={innerRef}>
            <ComboBox
                isCreatable
                isMulti
                closeMenuOnSelect
                isDisabled={isDisabled}
                isClearable={options?.some((v) => !v.isFixed)}
                styles={styles}
                options={options}
                value={options}
                inputValue={inputValue}
                placeholder={placeholder}
                classNames={{
                    dropdownIndicator: () => "!hidden",
                    clearIndicator: () => "!hidden",
                    multiValueLabel: () => "no-translate",
                    multiValueRemove: () => "no-translate",
                }}
                components={{ MultiValueLabel: CopyableMultiValueLabel }}
                menuIsOpen={false}
                onInputChange={handleInputChange}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onBlur={handleSubmit}
            />
        </div>
    );
};

const isCodeTag = (option: Option) => {
    return (
        option.type &&
        [TagType.GIFT, TagType.COUPON_CODE, TagType.MEMBERSHIP, TagType.STORE_CREDIT].includes(option.type)
    );
};

const parseLabelData = (data: Option): { data: string; label: string } => {
    if (data.type === TagType.MEMBERSHIP) {
        const code = data.label.slice(-6).toUpperCase();
        return { label: data.label.slice(0, -6) + code, data: code };
    }

    if (isCodeTag(data)) {
        const code = data.label.toUpperCase();
        return { label: code, data: code };
    }

    return { label: data.label, data: data.label };
};
